import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiErrorText, apiRequest, shortDate } from '../../utils';
import './CommsStats.css';

/* What the company's programmes have come to — all-time, grouped by programme.

   ★ The word is "answered yes", never "taking part". Two definitions of coming already exist in
   the platform — the RSVP button, and the four-leg test behind an event's own People screen — and
   they legitimately disagree. This counts the button, the same one the events stats counts, so a
   customer reading both screens never gets two answers to one question.

   ★ No attendance rate: somebody is only marked attended where a host actually checked people in,
   so an average across a year would report near-total absence for a company that simply does not
   scan.

   ★ All-time and no period tabs. A programme IS a window — it carries its own start and end — so
   filtering it by a second unrelated window would only hide part of the thing being measured. */

const Tile = ({ n, label }) => (
  <div className="cst-tile">
    <span className="cst-tile__n">{n}</span>
    <span className="cst-tile__label">{label}</span>
  </div>
);

const ProgrammeStats = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setData(null);
    setError(null);
    apiRequest('GET', '/company/programmes/stats')
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="bravos-cal__notice">{error}</p>;
  if (data === null) return <span className="auth__spinner" />;

  const rows = data.rows || [];

  return (
    <div className="cst">
      <div className="cst-tiles">
        <Tile n={data.programmes} label={t('statsProgrammes')} />
        <Tile n={data.running_now} label={t('statsProgrammesRunning')} />
        <Tile n={data.events} label={t('statsEventsUnder')} />
        <Tile n={data.past_events} label={t('statsEventsPast')} />
        <Tile n={data.going} label={t('statsAnsweredYes')} />
      </div>
      <p className="ce-note">{t('statsAnsweredNote')}</p>
      {/* ★ Said out loud rather than folded into the total above it: a league day is a real event
          this company ran, it just hangs under a competition instead of under a window of the
          year — so it appears in no row below and would otherwise read as a number gone missing. */}
      {data.league_events > 0 && (
        <p className="ce-note">{t('statsLeagueEvents', { n: data.league_events })}</p>
      )}

      {rows.length === 0 ? (
        <p className="ce-note mt-3">{t('statsNoProgrammesYet')}</p>
      ) : (
        <div className="cst-breakdown mt-2">
          {rows.map((row) => (
            <div key={row.id} className="cst-line">
              <span className="cst-line__label">
                <button type="button" className="gl-link"
                        onClick={() => navigate(`/programme/${row.id}`)}>
                  {row.name}
                </button>
                {row.running && (
                  <span className="ce-pill ce-pill--going ms-1">{t('statsRunningNow')}</span>
                )}
              </span>
              <span className="cst-line__sub">
                {[shortDate(row.date, i18n.language), shortDate(row.end_date, i18n.language)]
                  .filter(Boolean).join(' – ') || t('themeNoWindow')}
                {' · '}
                {t('statsNEvents', { count: row.events })}
              </span>
              <span className="cst-line__n">{row.going}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgrammeStats;
